import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/free-mode'

import { Box, capitalize } from '@mui/material'
import { format, getYear } from 'date-fns'
import { ru } from 'date-fns/locale'
import { forwardRef, useEffect } from 'react'
import { FreeMode } from 'swiper/modules'
import {
	type MonthNavigationCallbacks,
	type MonthNavigationRef,
	MonthSlide,
	useMonthData,
	useNavigationHandlers,
	useStickyHeaders
} from '~features/monthNavigation'
import { HEADER_GAP, SLIDE_PADDING } from '~features/monthNavigation/model/constants'
import { Typography } from '~sharedUI'

export const MonthSwiper = forwardRef<MonthNavigationRef, MonthNavigationCallbacks>(
	({ onCurrentMonthChange }, ref) => {
		const {
			swiperRef,
			months,
			emitCurrentMonth,
			handleMonthLoaded,
			tryAddNextMonth,
			tryAddPrevMonth
		} = useMonthData({ onCurrentMonthChange })

		const {
			updateStickyHeaders,
			startRAF,
			stopRAF,
			registerHeader,
			isProgrammaticScrollRef
		} = useStickyHeaders(swiperRef, months, emitCurrentMonth)

		useNavigationHandlers({
			ref,
			swiperRef,
			months,
			emitCurrentMonth,
			tryAddPrevMonth,
			isProgrammaticScrollRef
		})

		useEffect(() => {
			swiperRef.current?.update()
		}, [months.length])

		useEffect(() => {
			requestAnimationFrame(updateStickyHeaders)
		}, [months, updateStickyHeaders])
		return (
			<Box sx={{ width: '100%', height: '100%', userSelect: 'none' }}>
				<Swiper
					onSwiper={(swiper) => {
						swiperRef.current = swiper
						emitCurrentMonth()
						updateStickyHeaders()
					}}
					onTouchStart={startRAF}
					onTouchEnd={() => {
						stopRAF()
						emitCurrentMonth()
					}}
					onSliderMove={startRAF}
					onTransitionStart={startRAF}
					onTransitionEnd={() => {
						stopRAF()
						emitCurrentMonth()
					}}
					onProgress={tryAddNextMonth}
					onReachEnd={tryAddNextMonth}
					onResize={updateStickyHeaders}
					slidesPerView='auto'
					spaceBetween={30}
					freeMode={{
						enabled: true,
						momentumRatio: 0.2
					}}
					speed={800}
					modules={[FreeMode]}
					style={{ width: '100%', height: '100%' }}
				>
					{months.map((month) => {
						const monthName = capitalize(format(month.date, 'LLLL', { locale: ru }))
						const year = getYear(month.date)

						return (
							<SwiperSlide
								key={month.id}
								style={{
									width: 'auto',
									height: '100%',
									flexShrink: 1,
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'end',
									gap: '32px',
									paddingLeft: SLIDE_PADDING,
									paddingRight: SLIDE_PADDING,
									position: 'relative'
								}}
							>
								<Box
									ref={(el: HTMLDivElement | null) => registerHeader(month.id, el)}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: `${HEADER_GAP}px`,
										position: 'absolute',
										top: 0,
										left: SLIDE_PADDING,
										right: SLIDE_PADDING,
										overflow: 'hidden',
										visibility: month.isLoaded ? undefined : 'hidden'
									}}
								>
									<Typography
										data-sticky-title
										color='text.accent'
										fontWeight='bold'
										lineHeight={1.125}
										fontSize='xxl'
										sx={{
											flexShrink: 0,
											whiteSpace: 'nowrap',
											willChange: 'transform'
										}}
									>
										{monthName} {year}
									</Typography>

									<Box
										data-sticky-divider
										sx={{
											height: '2px',
											backgroundColor: 'text.accent',
											flexShrink: 0,
											willChange: 'transform, width'
										}}
									/>
								</Box>

								<MonthSlide
									monthId={month.id}
									date={month.date}
									period={month.period}
									onLoaded={handleMonthLoaded}
								/>
							</SwiperSlide>
						)
					})}
				</Swiper>
			</Box>
		)
	}
)

MonthSwiper.displayName = 'MonthSwiper'
